/** @file The auto-rig ghost: a proposed document compiled apart, posed at setup, drawn translucent. */
import { createCompileCache } from '@pose2d/compiler/incremental/createCompileCache';
import { recompile } from '@pose2d/compiler/incremental/recompile';
import type { TextureLayout } from '@pose2d/compiler/textureLayout';
import { loadSkeleton } from '@pose2d/core/load/loadSkeleton';
import { createPose } from '@pose2d/core/pose/createPose';
import type { Pose, SkeletonData, SkinData } from '@pose2d/core/types';
import { docIndex } from '@pose2d/format/doc/docIndex';
import type { Doc } from '@pose2d/format/types';
import { buildRender } from '@pose2d/geometry/buildRender';
import { createRenderBuffers } from '@pose2d/geometry/createRenderBuffers';
import { createTexturePages } from '@pose2d/geometry/createTexturePages';
import { RENDER_LAYOUT } from '@pose2d/geometry/renderBuffers';
import type { RenderBuffers } from '@pose2d/geometry/types';
import type { OverlayOptions } from '@pose2d/overlay/types';
import { poseAt } from '../../../playback/poseAt';
import type { ViewportGhost } from '../../../stores/ghostStore';
import { fadeChunk } from './fadeChunk';
import type { PageSet } from './pageSet';

/** One ghost, built when ghostStore changes (W3-33); frameChunks draws it after the real rig, never picks it. */
export interface GhostScene {
  data: SkeletonData;
  /** The ghost document's setup pose. */
  pose: Pose;
  /** Its attachments, light alpha × opacity, each draw on the viewport page holding the same image (sha256). */
  skeleton: RenderBuffers;
  /** Its overlay chunks, rebuilt (and faded) every frame by frameChunks. */
  overlay: RenderBuffers[];
  /** `ghost.overlays` as options, every bone and slot selected, so they draw in `OVERLAY_STYLE.selected`. */
  options: OverlayOptions;
  opacity: number;
}

const IDENTITY: TextureLayout = { kind: 'identity' };
const NO_SKIN: SkinData = { name: '', map: new Map() };
const DS = RENDER_LAYOUT.DRAW_STRIDE;
/** The ghost's own compile cache (the compiled store keeps the real document's); reset when the ghost goes. */
let cache = createCompileCache();

/** The ghost document as SkeletonData (identity pages, like the compiled store); throws when it does not compile. */
function compiled(doc: Doc): SkeletonData {
  const { json, atlas } = recompile(cache, doc, docIndex(doc), IDENTITY);
  return loadSkeleton(json, atlas);
}

/** Points every draw at the viewport page of the same image; a draw whose image has no viewport page is dropped. */
function sharePages(chunk: RenderBuffers, data: SkeletonData, pages: PageSet): void {
  const { textures } = pages;
  const index = new Map<string, number>();
  textures.pages.slice(0, textures.white).forEach((page, i) => { if (!index.has(page.id)) index.set(page.id, i); });
  let kept = 0;
  for (let d = 0; d < chunk.drawCount; d++) {
    const page = index.get(data.pages[chunk.draws[d * DS] ?? -1]?.id ?? '');
    if (page === undefined) continue;
    chunk.draws.copyWithin(kept * DS, d * DS, (d + 1) * DS);
    chunk.draws[kept * DS] = page;
    kept += 1;
  }
  chunk.drawCount = kept;
  chunk.topologyVersion += 1;
}

/** The ghost's attachments at its setup pose, on the viewport's pages, at `opacity`. */
function attachments(data: SkeletonData, pose: Pose, pages: PageSet, opacity: number): RenderBuffers {
  const skeleton = createRenderBuffers(data);
  const fontPage = pages.textures.pages[pages.textures.font];
  const textures = createTexturePages(data, { w: fontPage?.w ?? 1, h: fontPage?.h ?? 1 });
  buildRender(skeleton, { pose, data, skin: data.skins[data.defaultSkin] ?? NO_SKIN, textures });
  sharePages(skeleton, data, pages);
  fadeChunk(skeleton, opacity);
  return skeleton;
}

/** bones / names (bone names) / mesh as asked, everything selected: the ghost is told apart by colour. */
function optionsOf(ghost: ViewportGhost, data: SkeletonData): OverlayOptions {
  const on = (layer: ViewportGhost['overlays'][number]): boolean => ghost.overlays.includes(layer);
  const selection = { bones: data.bones.map((_, i) => i), slots: data.slots.map((_, i) => i) };
  return { bones: on('bones'), boneNames: on('names'), mesh: on('mesh'), selection };
}

/**
 * The ghost drawn over the real rig, sharing its page table `pages` (no texture is fetched for it); null without a
 * ghost (the compile cache is dropped then) or when its document does not compile or load (the panel shows that).
 */
export function ghostScene(ghost: ViewportGhost | null, pages: PageSet): GhostScene | null {
  if (ghost === null) {
    cache = createCompileCache();
    return null;
  }
  try {
    const data = compiled(ghost.doc);
    const pose = createPose(data);
    poseAt(data, pose, [], { kind: 'setup' });
    return { data, pose, skeleton: attachments(data, pose, pages, ghost.opacity), overlay: [], options: optionsOf(ghost, data), opacity: ghost.opacity };
  } catch {
    return null;
  }
}
